import React, { useId } from "react";

import { useController, useFormContext } from "react-hook-form";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/src/components/ui/field";
import { Input } from "@/src/components/ui/input";

import type { ChangeEvent, JSX } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface TextFieldBaseProps {
  label?: string;
  value: string;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  type?: "text" | "password" | "number";
  onChange: (value: string) => void;
  onBlur?: () => void;
  "data-tooltip-content"?: string;
  "data-tooltip-id"?: string;
}

export function TextFieldBase({
  label,
  value,
  placeholder,
  description,
  disabled,
  error,
  type,
  onChange,
  onBlur,
  "data-tooltip-content": tooltipContent,
  "data-tooltip-id": tooltipId,
}: TextFieldBaseProps): JSX.Element {
  const id = useId();

  return (
    <Field data-tooltip-content={tooltipContent} data-tooltip-id={tooltipId}>
      {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
      <Input
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
      />
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError>{error}</FieldError>
    </Field>
  );
}
interface TextFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, string | undefined>;
  label: string;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  type?: "text" | "password" | "number";
}

export function TextField<T extends FieldValues>({
  name,
  label,
  placeholder,
  description,
  disabled,
  type,
}: TextFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <TextFieldBase
      description={description}
      disabled={disabled}
      error={fieldState.error?.message}
      label={label}
      onChange={field.onChange}
      placeholder={placeholder}
      type={type}
      value={field.value ?? ""}
    />
  );
}
